import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { api, mediaUrl } from "@/lib/api";
import { PRODUCTS_CATALOG, CATEGORIES, SPEC_KEYS, HERO_IMG } from "@/lib/constants";
import { useContent } from "@/context/ContentContext";
import { X } from "lucide-react";

const ColorDots = ({ colors = [] }) => (
  <div className="flex items-center gap-1.5">
    {colors.map((c, i) => (
      <span key={i} className="h-4 w-4 rounded-full border border-white/20" style={{ backgroundColor: c }} title={c} />
    ))}
  </div>
);

const ProductCard = ({ p, onOpen }) => (
  <button
    data-testid={`product-card-${p.id}`}
    onClick={() => onOpen(p)}
    className="group text-left rounded-xl border border-[#232f25] bg-[#111712] overflow-hidden hover:border-[#8CE63C]/50 transition-all hover:-translate-y-1"
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-[#0d120e]">
      <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
      <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-[#0a0d0a]/80 text-[#8CE63C] border border-[#8CE63C]/30">
        {p.category}
      </span>
      {p.badge ? (
        <span className={`absolute top-3 right-3 font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${p.badge === "Best Seller" ? "bg-[#8CE63C] text-[#080b08]" : "bg-[#EA580C] text-white"}`}>
          {p.badge}
        </span>
      ) : null}
      {p.status === "out_of_stock" ? (
        <span className="absolute bottom-3 left-3 font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#261D1D] text-[#FCA5A5] border border-[#F87171]/50">Out of Stock</span>
      ) : null}
    </div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-xl font-bold text-white tracking-tight">{p.name}</h3>
        <span className="font-display text-lg font-black text-[#8CE63C] whitespace-nowrap">${p.price}</span>
      </div>
      <p className="text-xs text-[#9EABA0] mt-1">{p.specs?.Motor} · {p.specs?.Battery} · {p.specs?.["Tire Size"]}</p>
      <div className="flex items-center justify-between mt-4">
        <ColorDots colors={p.colors} />
        <span className="text-xs font-semibold text-[#8CE63C] group-hover:underline">View Details →</span>
      </div>
    </div>
  </button>
);

const ProductModal = ({ p, onClose }) => {
  const [buying, setBuying] = useState(false);
  if (!p) return null;
  const keys = [...SPEC_KEYS, ...Object.keys(p.specs || {}).filter((k) => !SPEC_KEYS.includes(k))];
  const buyNow = async () => {
    if (buying) return;
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", { product_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBuying(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[#232f25] bg-[#0a0d0a] erp-fade-up">
        <button data-testid="product-modal-close" onClick={onClose} className="sticky top-4 left-full ml-[-52px] z-10 h-9 w-9 rounded-full bg-[#111712] border border-[#232f25] text-[#9EABA0] hover:text-white flex items-center justify-center">
          <X size={18} />
        </button>
        <div className="p-6 sm:p-8 -mt-12">
          <div className="aspect-[16/9] rounded-xl overflow-hidden bg-[#0d120e] mb-6">
            <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-wider text-[#8CE63C]">{p.category}</span>
              <h2 className="font-display text-3xl font-black text-white uppercase tracking-tight">{p.name}</h2>
              {p.badge ? <span className={`inline-block mt-1 font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${p.badge === "Best Seller" ? "bg-[#8CE63C] text-[#080b08]" : "bg-[#EA580C] text-white"}`}>{p.badge}</span> : null}
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-black text-[#8CE63C]">${p.price}</div>
              <div className="text-[10px] text-[#69776b] uppercase tracking-wider">Reference Price</div>
            </div>
          </div>
          {p.colors?.length > 0 && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs text-[#69776b] uppercase tracking-wide">Colors:</span>
              <ColorDots colors={p.colors} />
            </div>
          )}
          <p className="text-sm text-[#9EABA0] leading-relaxed mt-5">{p.description}</p>

          <h3 className="font-display text-sm font-bold uppercase tracking-wide text-[#8CE63C] mt-7 mb-3">Specifications</h3>
          <div className="rounded-xl border border-[#232f25] overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {keys.filter((k) => p.specs?.[k]).map((k, i) => (
                  <tr key={k} className={i % 2 ? "bg-[#0d120e]" : "bg-[#111712]"}>
                    <td className="px-4 py-2.5 font-semibold text-[#9EABA0] w-2/5 border-b border-[#1a211b]">{k}</td>
                    <td className="px-4 py-2.5 text-[#f4f7f4] border-b border-[#1a211b]">{p.specs[k]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(p.images && p.images.length > 1) ? (
            <div className="flex gap-2 mt-3 flex-wrap">
              {p.images.slice(0, 6).map((im, i) => (
                <img key={i} src={mediaUrl(im)} alt="" className="h-14 w-20 object-cover rounded border border-[#232f25]" />
              ))}
            </div>
          ) : null}
          {p.warranty ? (
            <div className="mt-5 rounded-lg border border-[#232f25] bg-[#0d120e] p-4">
              <h4 className="font-mono text-[10px] uppercase tracking-wider text-[#8CE63C] mb-1">Warranty</h4>
              <p className="text-sm text-[#9EABA0] leading-relaxed">{p.warranty}</p>
            </div>
          ) : null}

          <div className="mt-7 space-y-3">
            <button data-testid="buy-now-button" onClick={buyNow} disabled={buying || p.status !== "available"} className="w-full px-5 py-3.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-black uppercase tracking-wider hover:bg-[#9df04f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {p.status !== "available" ? "Out of Stock" : buying ? "Starting checkout…" : `Buy Now — $${p.price}`}
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/special-requests" onClick={onClose} className="flex-1 text-center px-5 py-3 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors">Request Custom</Link>
              <Link to="/dealers" onClick={onClose} className="flex-1 text-center px-5 py-3 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors">Dealer / Bulk Inquiry</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Products() {
  const c = useContent();
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState(PRODUCTS_CATALOG);

  useEffect(() => {
    api.get("/products").then((r) => {
      if (Array.isArray(r.data) && r.data.length) setProducts(r.data);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (cat === "All" ? products : products.filter((p) => p.category === cat)),
    [cat, products]
  );

  return (
    <Layout>
      <header className="relative overflow-hidden border-b border-[#232f25]">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0d0a]/70 via-[#0a0d0a]/85 to-[#0a0d0a]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#8CE63C]">{c.hero?.eyebrow}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-tight mt-4 max-w-3xl">
            {c.hero?.title}
          </h1>
          <p className="text-[#9EABA0] text-sm sm:text-base leading-relaxed max-w-2xl mt-5">
            {c.hero?.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <a href="#catalog" className="px-5 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold uppercase tracking-wide hover:bg-[#9df04f] transition-colors">Browse Models</a>
            <Link to="/special-requests" className="px-5 py-2.5 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors">Special Request</Link>
          </div>
        </div>
      </header>

      <section id="catalog" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`product-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                cat === c ? "bg-[#8CE63C] border-[#8CE63C] text-[#080b08]" : "bg-[#0d120e] border-[#232f25] text-[#9EABA0] hover:border-[#8CE63C]/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
          {filtered.map((p) => (
            <ProductCard key={p.id} p={p} onOpen={setSelected} />
          ))}
        </div>
      </section>

      <ProductModal p={selected} onClose={() => setSelected(null)} />
    </Layout>
  );
}
